import { useRef, useState, type FocusEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { AnalyticsUser, AnalyticsUserDay } from '../api/adminAnalyticsApi';
import { fmtCalendarDay, fmtInt, fmtRub } from '../model/format';
import { SERIES_COLORS } from './TimeChart';

/** Порядковая шкала одного тона (dataviz, проверено на #1e1e20): больше генераций — светлее. */
export const HEAT_STEPS = ['#184f95', '#256abf', '#3987e5', '#6da7ec', '#b7d3f6'] as const;
const EMPTY = '#26262b';
/** Точка в клетке — в этот день были ходы игры или проверка грамматики: цвет игры с графика расходов. */
const PLAY = SERIES_COLORS[1];

const played = (cell: AnalyticsUserDay | null) => !!cell && cell.gameTurns + cell.grammarChecks > 0;

function heat(value: number, max: number): string {
  if (!value || !max) return EMPTY;
  const index = Math.min(HEAT_STEPS.length - 1, Math.floor((value / max) * HEAT_STEPS.length - 1e-9));
  return HEAT_STEPS[Math.max(0, index)];
}


interface HoverCell {
  user: AnalyticsUser;
  cell: AnalyticsUserDay | null;
  day: string;
  x: number;
  y: number;
}

/** Пользователи × дни: сколько слов сгенерировано, с кругами модели, подборками, лимитом и ₽. Ходы игры и проверки
 *  грамматики в слова не входят (2026-11-01: «66 слов» оказались ходами игры) — точка в клетке и своя строка с ₽. */
export function UserDayHeatmap({ users, days, lang }: { users: AnalyticsUser[]; days: string[]; lang: string }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState<HoverCell | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const show = (event: PointerEvent<HTMLDivElement> | FocusEvent<HTMLDivElement>, user: AnalyticsUser, cell: AnalyticsUserDay | null, day: string) => {
    const box = event.currentTarget.getBoundingClientRect();
    const frame = host.current?.getBoundingClientRect();
    if (!frame) return;
    setHover({ user, cell, day, x: box.left - frame.left + box.width / 2, y: box.bottom - frame.top });
  };
  const max = Math.max(0, ...users.flatMap((user) => user.days.map((cell) => cell.jobs)));
  const labelEvery = Math.max(1, Math.ceil(days.length / 8));

  if (!users.length) return <div className="py-8 text-center text-[14px] text-muted">{t('admin.analytics.heatmap.empty')}</div>;

  const columns = `minmax(150px, 220px) repeat(${days.length}, minmax(9px, 1fr)) minmax(150px, 220px)`;
  return (
    <div ref={host} className="relative" onPointerLeave={() => setHover(null)}>
      <div className="overflow-x-auto pb-1">
        <div className="grid min-w-[720px] items-center gap-x-[2px] gap-y-[3px]" style={{ gridTemplateColumns: columns }}>
          <div />
          {days.map((day, i) => (
            <div key={day} className="relative h-4">
              {(i % labelEvery === 0 || i === days.length - 1) && (
                <span className="absolute left-0 top-0 whitespace-nowrap text-[12px] text-muted">{fmtCalendarDay(day, lang)}</span>
              )}
            </div>
          ))}
          <div className="pl-3 text-right text-[12px] text-muted">{t('admin.analytics.heatmap.total')}</div>

          {users.map((user) => {
            const byDay = new Map(user.days.map((cell) => [cell.day, cell]));
            return (
              <div key={user.id} className="contents">
                <div className="amp-mask ym-hide-content min-w-0 pr-3">
                  <div className="truncate text-[14px] text-fg">{user.name || user.email}</div>
                  <div className="truncate text-[12px] text-muted">
                    {user.plan === 'PREMIUM' ? 'Max' : user.plan === 'PRO' ? 'Pro' : 'Demo'} · {user.email}
                  </div>
                </div>
                {days.map((day) => {
                  const cell = byDay.get(day) ?? null;
                  return (
                    <div
                      key={day}
                      tabIndex={cell ? 0 : -1}
                      onPointerEnter={(event) => show(event, user, cell, day)}
                      onFocus={(event) => show(event, user, cell, day)}
                      onBlur={() => setHover(null)}
                      className={cn(
                        'relative h-[26px] rounded-[3px] outline-none transition-[filter] focus-visible:ring-2 focus-visible:ring-accent',
                        hover?.user.id === user.id && hover.day === day && 'brightness-125',
                      )}
                      style={{ background: heat(cell?.jobs ?? 0, max) }}
                      aria-label={`${user.name || user.email}, ${fmtCalendarDay(day, lang)}: ${t('admin.analytics.heatmap.words', { count: cell?.jobs ?? 0 })}${
                        played(cell) ? `, ${t('admin.analytics.heatmap.played')}` : ''
                      }`}
                    >
                      {played(cell) && (
                        <span
                          className="absolute left-1/2 top-1/2 h-[6px] w-[6px] -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_0_1px_#1e1e20]"
                          style={{ background: PLAY }}
                        />
                      )}
                    </div>
                  );
                })}
                <div className="pl-3 text-right">
                  <div className="text-[14px] font-semibold tabular-nums text-fg">
                    {t('admin.analytics.heatmap.words', { count: user.jobs })}
                  </div>
                  {/* только играл — строку «0 ₽ за слова» не показываем */}
                  {(user.jobs > 0 || user.topicJobs > 0 || !(user.gameTurns > 0 || user.grammarChecks > 0)) && (
                    <div className="text-[12px] tabular-nums text-muted">
                      {user.jobsRetry > 0 && <>{t('admin.analytics.heatmap.retry', { n: fmtInt(user.jobsRetry) })} · </>}
                      {user.topicJobs > 0 && <>{t('admin.analytics.heatmap.topics', { n: fmtInt(user.topicJobs) })} · </>}
                      {fmtRub(user.spendRub)}
                    </div>
                  )}
                  {user.gameTurns > 0 && (
                    <div className="text-[12px] tabular-nums text-muted">
                      {t('admin.analytics.heatmap.game', { count: user.gameTurns, rub: fmtRub(user.gameRub) })}
                    </div>
                  )}
                  {user.grammarChecks > 0 && (
                    <div className="text-[12px] tabular-nums text-muted">
                      {t('admin.analytics.heatmap.grammar', { count: user.grammarChecks, rub: fmtRub(user.grammarRub) })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
        <span>{t('admin.analytics.heatmap.scale')}</span>
        <span className="inline-flex items-center gap-[2px]">
          <span className="h-2.5 w-4 rounded-[2px]" style={{ background: EMPTY }} />
          {HEAT_STEPS.map((color) => (
            <span key={color} className="h-2.5 w-4 rounded-[2px]" style={{ background: color }} />
          ))}
        </span>
        <span className="tabular-nums">0 — {fmtInt(max)}</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-[6px] w-[6px] rounded-full" style={{ background: PLAY }} />
          {t('admin.analytics.heatmap.playedLegend')}
        </span>
      </div>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 w-[220px] rounded-xl bg-[#26262b] px-3 py-2.5 text-[12px] shadow-[0_0_0_1px_#34343a,0_16px_32px_-12px_rgba(0,0,0,0.8)]"
          style={{ left: Math.max(0, hover.x - 110), top: hover.y + 6 }}
        >
          <div className="amp-mask ym-hide-content truncate text-muted">
            {hover.user.name || hover.user.email} · {fmtCalendarDay(hover.day, lang)}
          </div>
          <div className="mt-1 text-[14px] font-semibold tabular-nums text-fg">
            {t('admin.analytics.heatmap.words', { count: hover.cell?.jobs ?? 0 })}
          </div>
          {hover.cell && (
            <div className="mt-1 space-y-0.5 tabular-nums text-muted">
              <div>{t('admin.analytics.heatmap.rounds', { two: hover.cell.jobsTwo, three: hover.cell.jobsThreePlus })}</div>
              {hover.cell.topicJobs > 0 && <div>{t('admin.analytics.heatmap.topics', { n: hover.cell.topicJobs })}</div>}
              <div>
                {t('admin.analytics.heatmap.units', { n: hover.cell.units === null ? '—' : fmtInt(hover.cell.units) })} ·{' '}
                {fmtRub(hover.cell.spendRub)}
              </div>
              {hover.cell.gameTurns > 0 && (
                <div>{t('admin.analytics.heatmap.game', { count: hover.cell.gameTurns, rub: fmtRub(hover.cell.gameRub) })}</div>
              )}
              {hover.cell.grammarChecks > 0 && (
                <div>
                  {t('admin.analytics.heatmap.grammar', { count: hover.cell.grammarChecks, rub: fmtRub(hover.cell.grammarRub) })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
